"use client";

import { type RefObject, useCallback, useState } from "react";
import { FileTextIcon, SheetIcon } from "lucide-react";
import { useAuiState } from "@assistant-ui/react";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Spinner } from "@/components/ui/spinner";
import {
  extractTableData,
  downloadCsv,
  downloadExcel,
} from "@/lib/tableExport";
import { ClientLogger } from "@/lib/clientLogger";
import { useTranslation } from "react-i18next";

export function TableExportHeader({
  tableRef,
}: {
  tableRef: RefObject<HTMLTableElement | null>;
}) {
  const { t } = useTranslation();
  const isRunning = useAuiState(({ thread }) => thread?.isRunning);
  const [isExporting, setIsExporting] = useState(false);

  const handleCsv = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    const data = extractTableData(table);
    if (!data) return;
    downloadCsv(data);
  }, [tableRef]);

  const handleExcel = useCallback(async () => {
    const table = tableRef.current;
    if (!table) return;
    const data = extractTableData(table);
    if (!data) return;
    setIsExporting(true);
    try {
      await downloadExcel(data);
    } catch (e) {
      ClientLogger.error("Excel export failed", {
        error: e instanceof Error ? e : undefined, component: "TableExportHeader",
      });
    }finally {
      setIsExporting(false);
    }
  }, [tableRef]);

  if (isRunning) return null;

  return (
    <div className="absolute top-1 right-1 z-10 flex gap-0.5 rounded-md border border-border/60 bg-background/90 p-0.5 opacity-0 shadow-sm backdrop-blur-sm transition-opacity group-hover:opacity-100">
      <TooltipIconButton tooltip={t("export.csv")} onClick={handleCsv}>
        <FileTextIcon className="size-3.5" />
      </TooltipIconButton>
      <TooltipIconButton
        tooltip={t("export.excel")}
        onClick={handleExcel}
        disabled={isExporting}
      >
        {isExporting ? (
          <Spinner size="sm" />
        ) : (
          <SheetIcon className="size-3.5" />
        )}
      </TooltipIconButton>
    </div>
  );
}
